import React, { useEffect } from 'react';
import { Container, Form, Col, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { object, string } from 'yup';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import { TextInput, SelectInput } from '../../../../Helpers/Inputs';
import States from '../../../../Helpers/Lists/States';
import BackNext from '../../../Common/SubComponents/BackNext';
import { customValidators } from '../../../../Helpers/Validators/customValidators';

export default function Payment(props) {
  const { register, errors, handleSubmit, setValue, triggerValidation } = useForm({
    validationSchema: Payment.ValidationSchema,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    // manually register fields for validation that don't play nice with react-hook-form register
    register({ name: 'payor' });
    setValue('payor', props.formState.payor);

    register({ name: 'state' });
    setValue('state', props.formState.state);

    register({ name: 'paymentMethod' });
    setValue('paymentMethod', props.formState.paymentMethod);

    register({ name: 'bankState' });
    setValue('bankState', props.formState.bankState);
  }, [register]);

  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value) => {
    setValue(field, value);
    props.onFieldChange(field, value);
    triggerValidation(field);
  };

  const payors = [{ value: `${props.insuredInfo.firstName} ${props.insuredInfo.lastName}`, label: `${props.insuredInfo.firstName} ${props.insuredInfo.lastName}` }];

  return (
    <Container>
      <Row className="justify-content-center">
        <Col lg={5}>
          <div className="formPageTitle">Payment Information</div>

          <form className="quote-form" onSubmit={handleSubmit(props.onNext)}>
            <Form.Group>
              <SelectInput
                name="payor"
                id="payor"
                value={props.formState.payor}
                onChange={(e) => handleValueChange('payor', e.target.value)}
                label="Payor"
                selectOptions={payors}
                errors={errors && errors.payor}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="address"
                id="address"
                label="Address"
                value={props.formState.address}
                onChange={(e) => props.onFieldChange('address', e.target.value)}
                errors={errors.address}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="address2"
                id="address2"
                label="Apt or Suite (Optional)"
                value={props.formState.address2}
                onChange={(e) => props.onFieldChange('address2', e.target.value)}
                errors={errors.address2}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="city"
                id="city"
                label="City"
                value={props.formState.city}
                onChange={(e) => props.onFieldChange('city', e.target.value)}
                errors={errors.city}
                inputRef={register}
              />
            </Form.Group>

            <Row>
              <Col>
                <Form.Group>
                  <SelectInput
                    name="state"
                    id="state"
                    value={props.formState.state}
                    onChange={(e) => handleValueChange('state', e.target.value)}
                    label="State"
                    selectOptions={States()}
                    inputRef={register}
                    errors={errors && errors.state}
                  />
                </Form.Group>
              </Col>

              <Col>
                <Form.Group>
                  <TextInput
                    name="zip"
                    id="zip"
                    label="Zip"
                    value={props.formState.zip}
                    onChange={(e) => props.onFieldChange('zip', e.target.value)}
                    errors={errors.zip}
                    inputRef={register}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group>
              <SelectInput
                name="paymentMethod"
                id="paymentMethod"
                value={props.formState.paymentMethod}
                onChange={(e) => handleValueChange('paymentMethod', e.target.value)}
                label="Payment Method"
                selectOptions={[
                  { value: 'checking', label: 'EFT Draft - Checking Account' },
                  { value: 'savings', label: 'EFT Draft - Savings Account' },
                ]}
                errors={errors && errors.paymentMethod}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="bankName"
                id="bankName"
                label="Financial Institution Name"
                value={props.formState.bankName}
                onChange={(e) => props.onFieldChange('bankName', e.target.value)}
                errors={errors.bankName}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="bankCity"
                id="bankCity"
                label="Financial Institution City"
                value={props.formState.bankCity}
                onChange={(e) => props.onFieldChange('bankCity', e.target.value)}
                errors={errors.bankCity}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <SelectInput
                name="bankState"
                id="bankState"
                value={props.formState.bankState}
                onChange={(e) => handleValueChange('bankState', e.target.value)}
                label="Financial Institution State"
                selectOptions={States()}
                errors={errors && errors.bankState}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="routingNumber"
                id="routingNumber"
                label="Routing Number"
                value={props.formState.routingNumber}
                onChange={(e) => props.onFieldChange('routingNumber', e.target.value)}
                errors={errors.routingNumber}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <TextInput
                name="accountNumber"
                id="accountNumber"
                label="Account Number"
                value={props.formState.accountNumber}
                onChange={(e) => props.onFieldChange('accountNumber', e.target.value)}
                errors={errors.accountNumber}
                inputRef={register}
              />
            </Form.Group>

            <BackNext onBack={props.onBack} />
          </form>
        </Col>
      </Row>
    </Container>
  );
}

Payment.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
};

Payment.ValidationSchema = object().shape({
  payor: string().required('Payor is required.'),
  address: customValidators.string().isValidNameLimitedSpecialChar('Address is required.', true, true),
  address2: customValidators.string().isValidNameLimitedSpecialChar('', false, true),
  city: customValidators.string().isValidNameLimitedSpecialChar('City is required.', true, false),
  state: string().required('State is required.'),
  zip: string()
    .required('Zip code is required.')
    .matches(/^[0-9]{5}$/, 'Zip code must be 5 number characters.'),
  paymentMethod: string().required('Payment Method is required.'),
  bankName: customValidators.string().isValidNameLimitedSpecialChar('Financial Institution Name is required.', true, false),
  bankCity: customValidators.string().isValidNameLimitedSpecialChar('Financial Institution City is required.', true, false),
  bankState: string().required('Financial Institution State is required.'),
  routingNumber: string()
    .required('Routing Number is required.')
    .matches(/^[0-9]{9}$/, 'Routing number must be 9 number characters.'),
  accountNumber: string()
    .required('Account Number is required.')
    .matches(/^[0-9]{1,17}$/, 'Account number must be numbers with a max of 17 characters.'),
});

Payment.DefaultFormState = (prefill = false) => {
  return {
    payor: prefill ? 'Reymundo Puga' : '',
    address: prefill ? '123 Main Street' : '',
    address2: prefill ? '#123' : '',
    city: prefill ? 'Atlanta' : '',
    state: prefill ? 'ID' : '',
    zip: prefill ? '83716' : '',
    paymentMethod: prefill ? 'checking' : '',
    bankName: prefill ? '1st Bank of Nowhere' : '',
    bankCity: prefill ? 'Atlanta' : '',
    bankState: prefill ? 'ID' : '',
    routingNumber: prefill ? '123456789' : '',
    accountNumber: prefill ? '11111111112' : '',
  };
};



// WEBPACK FOOTER //
// ./src/components/Quoters/Securian/WL/steps/Payment.js